// ── Remote session rules (TPT495) ──
//
// What the task view may offer for a task, given the devices online, the sessions the relay
// reports and the viewer's rights. Pure, so the rules run under node:test without a DOM. The relay
// enforces all of this again: these rules only decide what the user is offered.

// `exited`, `lost` and `revoked` end a session, and the relay forgets it at once.
const LIVE_STATES = new Set(['starting', 'running', 'disconnected']);
const AGENT_LABELS = { claude: 'Claude', codex: 'Codex', pi: 'Pi' };
const STATE_KEYS = { starting: 'stateStarting', running: 'stateRunning', disconnected: 'stateOffline' };

export const isLiveSession = session => LIVE_STATES.has(session?.state);

export const agentLabel = agent => AGENT_LABELS[agent] ?? agent ?? '';

// Task App's start handler refuses every other shape (task_not_startable).
export const canRunOnDevice = task => !!task && task.category === 'CODING' && !task.is_objective;

// The sessions shown for a task, in the order they started.
export function taskSessions(sessions, taskKey) {
  return [...sessions.values()]
    .filter(session => session.task_key === taskKey && isLiveSession(session))
    .sort((a, b) => String(a.started_at).localeCompare(String(b.started_at)));
}

export function startableDevices(devices, projectId) {
  const id = Number(projectId);
  return devices.filter(device => (device.startable_project_ids ?? []).includes(id)
    && (device.capabilities?.commands ?? []).includes('start'));
}

const deviceCan = (device, command) => (device?.capabilities?.commands ?? []).includes(command);

// One session row: `canAttach`/`canStop` follow the relay's rules (the device's owner attaches, an
// owner with task:write stops). A session that is still starting has no terminal to attach to yet.
function sessionRow(session, view, ctx) {
  const device = view.devices.find(candidate => candidate.id === session.device_id);
  const mine = session.owner_id === ctx.userId;
  return {
    id: session.session_id,
    deviceName: session.device_name,
    agent: agentLabel(session.agent),
    state: session.state,
    stateKey: STATE_KEYS[session.state],
    mine,
    canAttach: mine && session.state === 'running' && deviceCan(device, 'attach'),
    canStop: mine && ctx.canWrite && session.state !== 'disconnected' && deviceCan(device, 'stop'),
    stopping: view.stopping.has(session.session_id),
    confirming: view.confirmStopId === session.session_id,
  };
}

// The Start row, or null when this viewer has nothing to pick. `noteKey` says why Start is off.
function pickerModel(task, view, ctx, rows) {
  if (!ctx.canWrite) return { picker: null, noteKey: 'viewOnly' };
  if (rows.length) return { picker: null, noteKey: null }; // Attach it; never a second run.
  const devices = startableDevices(view.devices, view.projectId);
  if (!devices.length) return { picker: null, noteKey: 'noStartDevice' };
  const active = task.active_device;
  const reachable = active && view.devices.some(device => device.id === active.id);
  const blocked = !ctx.canStartTask ? 'assignedToOther' : active && !reachable ? 'runningElsewhere' : null;
  const selected = devices.find(device => device.id === view.deviceId) ?? devices[0];
  const agents = selected.capabilities?.agents ?? [];
  return {
    picker: {
      devices: devices.map(device => ({ id: device.id, name: device.name })),
      deviceId: selected.id,
      agents: agents.map(agent => ({ id: agent, label: agentLabel(agent) })),
      agent: agents.includes(view.agent) ? view.agent : '',
      starting: view.starting,
      // Off until the first snapshot: only that says whether the task already runs somewhere.
      canStart: !blocked && !view.starting && view.subscribed,
    },
    noteKey: blocked,
  };
}

// Everything renderRemoteSessionControls() draws. `kind`:
//   hidden   nothing to show for this task and viewer
//   checking the first online list is on its way
//   failed   that list could not be read
//   empty    no device of this viewer is online for the project
//   revoked  the relay ended this viewer's access to the project
//   ready    session rows and/or the picker
export function remoteControlsModel(task, view, ctx) {
  if (!view) return { kind: 'hidden' };
  const rows = taskSessions(view.sessions, task.task_key).map(session => sessionRow(session, view, ctx));
  if (view.status === 'revoked') return { kind: 'revoked' };
  if (rows.length === 0 && (ctx.closed || !ctx.canWrite)) return { kind: 'hidden' };
  if (view.status === 'checking') return { kind: 'checking' };
  if (view.status === 'failed') return { kind: 'failed' };
  if (view.devices.length === 0 && rows.length === 0) return { kind: 'empty', checking: view.checking };
  const { picker, noteKey } = ctx.closed ? { picker: null, noteKey: null } : pickerModel(task, view, ctx, rows);
  return {
    kind: 'ready',
    rows,
    picker,
    noteKey,
    noteParams: { device: task.active_device?.name ?? '' },
    connection: view.connection,
  };
}
